import { Injectable } from '@angular/core';
import { SERVER_API_URL } from '../../../app.constants';
import { ParamContext } from '../../service/param.context';
import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { createRequestOption } from '../..';
import { Transaction, TransactionStatus } from '../model/transaction.model';
import { map } from 'rxjs/operators';
import { TransactionSearch } from '../../../entities/transaction/transaction-search.model';
import { SearchComposite } from '../../model/search-composite';
import { TransactionType } from './transaction-type.model';
import { EnableLoadingIndicator } from '../../loading-indicator';

@Injectable({ providedIn: 'root' })
export class TransactionService {

    constructor(private context: ParamContext,
                private http: HttpClient) {
    }

    createConfirmedDeposits(lotDepositRequest: any) {
        return this.http.post<any>(
            SERVER_API_URL + `crops/${this.context.cropName}/transactions/confirmed-deposits/generation?programUUID=` + this.context.programUUID,
            lotDepositRequest);
    }

    createPendingDeposits(lotDepositRequest: any) {
        return this.http.post<any>(
            SERVER_API_URL + `crops/${this.context.cropName}/transactions/pending-deposits/generation?programUUID=` + this.context.programUUID,
            lotDepositRequest);
    }

    getTransactionsByGermplasmId(gid: number, lotId: number, request: any): Observable<HttpResponse<Transaction[]>> {
        if (this.context.programUUID) {
            request['programUUID'] = this.context.programUUID;
        }
        if (lotId) {
            request['lotId'] = lotId;
        }
        const url = SERVER_API_URL + `crops/${this.context.cropName}/germplasm/${gid}/transactions`;
        const params = createRequestOption(request);
        return this.http.get<Transaction[]>(url, { params, observe: 'response' });

    }

    getSearchResults(req?: any): Observable<HttpResponse<Transaction[]>> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/transactions/search?programUUID=` + this.context.programUUID;
        const options = createRequestOption(req);
        return this.http.get<Transaction[]>(url, { params: options, observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) })
            .pipe(map((res: HttpResponse<Transaction[]>) => res));
    }

    search(req?: TransactionSearch): Observable<string> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/transactions/search?programUUID=` + this.context.programUUID;
        return this.http.post<any>(url, req, { observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) })
            .pipe(map((res: any) => res.body.searchResultDbId));
    }

    createPendingWithdrawals(lotWithdrawalInputDto: any) {
        return this.http.post<any>(
            `${SERVER_API_URL}crops/${this.context.cropName}/transactions/pending-withdrawals/generation`, lotWithdrawalInputDto, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

    createConfirmedWithdrawals(lotWithdrawalInputDto: any) {
        return this.http.post<any>(
            `${SERVER_API_URL}crops/${this.context.cropName}/transactions/confirmed-withdrawals/generation`, lotWithdrawalInputDto, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

    confirmPendingTransaction(searchComposite: SearchComposite<any, any>) {
        return this.http.post<any>(
            `${SERVER_API_URL}crops/${this.context.cropName}/transactions/confirmation`, searchComposite, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

    updateTransactions(transactions) {
        return this.http.patch<any>(`${SERVER_API_URL}crops/${this.context.cropName}/pending-transactions?programUUID=` + this.context.programUUID,
            transactions , { observe: 'response' });
    }

    getAvailableBalance(lotId: number): Observable<HttpResponse<Transaction[]>> {
        return this.http.get<Transaction[]>(`${SERVER_API_URL}crops/${this.context.cropName}/lots/${lotId}/available-balance-transactions`, { observe: 'response' })
            .pipe(map((res: HttpResponse<Transaction[]>) => res));
    }

    exportTransactionsTemplate(req: any): Observable<HttpResponse<Blob>> {
        const options: HttpParams = createRequestOption(req);
        const url = SERVER_API_URL + `crops/${this.context.cropName}/transactions/xlsx?programUUID=` + this.context.programUUID;
        return this.http
            .get(url, {
                params: options,
                responseType: 'blob',
                observe: 'response'
            });
    }

    importConfirmedDeposits(lotDeposit: any) {
        return this.http.post<any>(
            `${SERVER_API_URL}crops/${this.context.cropName}/transactions/confirmed-deposits-lists`, lotDeposit, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

    importPendingDeposits(lotDeposit: any) {
        return this.http.post<any>(
            `${SERVER_API_URL}crops/${this.context.cropName}/transactions/pending-deposits-lists`, lotDeposit, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

    cancelPendingTransaction(searchComposite: SearchComposite<any, any>) {
        return this.http.post<any>(
            `${SERVER_API_URL}crops/${this.context.cropName}/transactions/cancellation`, searchComposite, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

    queryTransactionTypes(): Observable<TransactionType[]> {
        const cropName = this.context.cropName;
        return this.http.get<TransactionType[]>(SERVER_API_URL + `crops/${cropName}/transaction-types`,
            { observe: 'response' }).pipe(map((res: HttpResponse<any[]>) => res.body.map((body) => {
            return { id: body.id, value: body.value };
        })));
    }

    queryTransactionStatus(): Observable<TransactionStatus[]> {
        const cropName = this.context.cropName;
        return this.http.get<TransactionStatus[]>(SERVER_API_URL + `crops/${cropName}/transaction-status-types`,
            { observe: 'response' })
            .pipe(map((res: HttpResponse<any[]>) => res.body.map((body) => {
                return { id: body.intValue, name: body.value };
            })));
    }

    createAdjustments(lotAdjustmentRequestDto: any) {
        return this.http.post<any>(
            `${SERVER_API_URL}crops/${this.context.cropName}/transactions/confirmed-adjustments`, lotAdjustmentRequestDto, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

}
